import React, { useEffect, useMemo, useState } from "react";
import { AdminAPI } from "@/lib/api";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, Legend,
} from "recharts";
import { toast } from "sonner";

const RANGES = [
  { key: 7, label: "7 D" },
  { key: 30, label: "30 D" },
  { key: 90, label: "90 D" },
  { key: 365, label: "1 Y" },
];

const PIE_COLORS = ["#06B6D4", "#F59E0B", "#10B981", "#EF4444", "#8B5CF6", "#F472B6"];

function inrCompact(paise) {
  const rupees = (paise || 0) / 100;
  if (rupees >= 10_00_000) return `₹${(rupees / 1_00_000).toFixed(1)} L`;
  if (rupees >= 1_000) return `₹${(rupees / 1_000).toFixed(1)} K`;
  return `₹${rupees.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}
function inrFull(paise) {
  return `₹${((paise || 0) / 100).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}
function shortDate(iso) {
  // 2026-02-14 -> 14 Feb
  if (!iso || iso.length < 10) return iso || "";
  const d = new Date(iso + "T00:00:00Z");
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

export default function AnalyticsTab() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const fetchData = async () => {
      try {
        const r = await AdminAPI.analyticsOverview(days);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || "Failed to load analytics");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [days]);

  const series = useMemo(() => {
    if (!data?.timeseries) return [];
    return data.timeseries.map((d) => ({
      ...d,
      label: shortDate(d.date),
      revenue_inr: Math.round((d.revenue_paise || 0) / 100),
    }));
  }, [data]);

  if (loading && !data) return <div className="overline" data-testid="analytics-loading">Loading analytics…</div>;
  if (!data) return <div className="overline">No analytics data available.</div>;

  const k = data.kpis;

  return (
    <div data-testid="admin-analytics-tab">
      {/* Header + range picker */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ fontSize: 24 }}>Analytics</h2>
          <p style={{ color: "var(--v-text-2)", fontSize: 13, marginTop: 4, maxWidth: 640 }}>
            Internal product analytics computed live from MongoDB. For traffic-source
            and page-view data, log in to your Google Analytics console.
          </p>
        </div>
        <div role="tablist" data-testid="analytics-range-picker" style={{ display: "inline-flex", border: "1px solid var(--v-border)", borderRadius: 999, overflow: "hidden" }}>
          {RANGES.map((r) => (
            <button
              key={r.key}
              role="tab"
              data-testid={`analytics-range-${r.key}`}
              onClick={() => setDays(r.key)}
              style={{
                padding: "8px 16px",
                fontSize: 11,
                letterSpacing: ".12em",
                textTransform: "uppercase",
                border: "none",
                background: days === r.key ? "var(--v-accent)" : "transparent",
                color: days === r.key ? "#000" : "var(--v-text-2)",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI tiles */}
      <div style={{ marginTop: 28, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }} data-testid="analytics-kpis">
        <Kpi label="Lifetime revenue" value={inrFull(k.revenue_lifetime_paise)} sub={`${k.topups_lifetime} top-ups · ${k.credits_sold} credits`} testid="kpi-lifetime-revenue" highlight />
        <Kpi label={`Revenue · last ${days}d`} value={inrFull(k.revenue_window_paise)} sub={`${k.audits_in_window} audits in window`} testid="kpi-window-revenue" />
        <Kpi label="Total users" value={k.total_users} sub={`${k.internal_users} internal · ${k.external_users} external`} testid="kpi-total-users" />
        <Kpi label={`Signups · last ${days}d`} value={k.signups_in_window} sub={k.pending_approvals ? `${k.pending_approvals} awaiting approval` : "no pending approvals"} testid="kpi-signups" />
        <Kpi label="Projects" value={k.total_projects} sub={`${k.total_assessments} total audits`} testid="kpi-projects" />
        <Kpi label="Trial → paid" value={`${k.trial_conversion_pct}%`} sub={`${k.trials_converted} of ${k.trials_granted} trials converted`} testid="kpi-trial-conversion" />
        <Kpi label="Credits in circulation" value={k.credits_in_circulation} sub="unspent in user wallets" testid="kpi-credits-circulation" />
        <Kpi label={`Support · last ${days}d`} value={k.support_submissions_in_window} sub="tickets submitted" testid="kpi-support" />
      </div>

      {/* Signups vs Audits timeseries */}
      <div className="v-surface" style={{ padding: 24, marginTop: 28 }} data-testid="analytics-activity-chart">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div className="overline">Daily activity</div>
            <h3 style={{ fontSize: 18, marginTop: 4 }}>Signups + audits, last {days} days</h3>
          </div>
        </div>
        <div style={{ height: 280, marginTop: 16 }}>
          <ResponsiveContainer>
            <AreaChart data={series} margin={{ left: -8, right: 8, top: 8 }}>
              <defs>
                <linearGradient id="grad-signups" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#06B6D4" stopOpacity={0.6} />
                  <stop offset="100%" stopColor="#06B6D4" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="grad-audits" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#F59E0B" stopOpacity={0.6} />
                  <stop offset="100%" stopColor="#F59E0B" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="var(--v-border)" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: "var(--v-text-2)", fontSize: 11 }} interval="preserveStartEnd" />
              <YAxis tick={{ fill: "var(--v-text-2)", fontSize: 11 }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "var(--v-bg)", border: "1px solid var(--v-border)", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Area type="monotone" dataKey="signups" stroke="#06B6D4" fill="url(#grad-signups)" strokeWidth={2} />
              <Area type="monotone" dataKey="audits" stroke="#F59E0B" fill="url(#grad-audits)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Revenue timeseries */}
      <div className="v-surface" style={{ padding: 24, marginTop: 20 }} data-testid="analytics-revenue-chart">
        <div className="overline">Revenue trend</div>
        <h3 style={{ fontSize: 18, marginTop: 4 }}>Razorpay top-ups · ₹ INR, last {days} days</h3>
        <div style={{ height: 240, marginTop: 16 }}>
          <ResponsiveContainer>
            <LineChart data={series} margin={{ left: 0, right: 8, top: 8 }}>
              <CartesianGrid stroke="var(--v-border)" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: "var(--v-text-2)", fontSize: 11 }} interval="preserveStartEnd" />
              <YAxis tick={{ fill: "var(--v-text-2)", fontSize: 11 }} tickFormatter={(v) => inrCompact(v * 100)} />
              <Tooltip
                contentStyle={{ background: "var(--v-bg)", border: "1px solid var(--v-border)", fontSize: 12 }}
                formatter={(v) => [inrFull(v * 100), "Revenue"]}
              />
              <Line type="monotone" dataKey="revenue_inr" stroke="#10B981" strokeWidth={2} dot={{ r: 2 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Two-column: top brands, audit kinds */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 20, marginTop: 20 }}>
        <div className="v-surface" style={{ padding: 24 }} data-testid="analytics-top-brands">
          <div className="overline">Top brands</div>
          <h3 style={{ fontSize: 18, marginTop: 4 }}>Most-audited brands (all time)</h3>
          <div style={{ height: 280, marginTop: 16 }}>
            <ResponsiveContainer>
              <BarChart data={data.top_brands} layout="vertical" margin={{ left: 8, right: 16 }}>
                <CartesianGrid stroke="var(--v-border)" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fill: "var(--v-text-2)", fontSize: 11 }} />
                <YAxis type="category" dataKey="brand" width={130} tick={{ fill: "var(--v-text)", fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "var(--v-bg)", border: "1px solid var(--v-border)", fontSize: 12 }} />
                <Bar dataKey="audits" fill="var(--v-accent)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          {data.top_brands.length === 0 && <EmptyHint>No brand-audit data yet.</EmptyHint>}
        </div>

        <div className="v-surface" style={{ padding: 24 }} data-testid="analytics-audit-kinds">
          <div className="overline">Audit kinds</div>
          <h3 style={{ fontSize: 18, marginTop: 4 }}>How designers are using Verdict</h3>
          <div style={{ height: 280, marginTop: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>
            {data.audit_kinds.length > 0 ? (
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={data.audit_kinds}
                    dataKey="count"
                    nameKey="kind"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    innerRadius={50}
                    paddingAngle={2}
                    label={(p) => `${p.kind}`}
                    labelLine={false}
                  >
                    {data.audit_kinds.map((entry, idx) => (
                      <Cell key={entry.kind} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ background: "var(--v-bg)", border: "1px solid var(--v-border)", fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <EmptyHint>No audit-kind data yet.</EmptyHint>
            )}
          </div>
        </div>
      </div>

      {/* Top designers */}
      <div className="v-surface" style={{ padding: 24, marginTop: 20 }} data-testid="analytics-top-designers">
        <div className="overline">Active designers</div>
        <h3 style={{ fontSize: 18, marginTop: 4 }}>Most audits run · last {days} days</h3>
        {data.top_designers.length === 0 ? (
          <EmptyHint style={{ marginTop: 16 }}>No audits run in this window yet.</EmptyHint>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 14 }}>
            <thead>
              <tr style={{ textAlign: "left", color: "var(--v-text-2)", fontSize: 11, letterSpacing: ".12em", textTransform: "uppercase" }}>
                <th style={{ padding: "10px 12px" }}>Designer</th>
                <th style={{ padding: "10px 12px", textAlign: "right" }}>Audits</th>
              </tr>
            </thead>
            <tbody>
              {data.top_designers.map((d) => (
                <tr key={d.name + d.audits} style={{ borderTop: "1px solid var(--v-border)" }} data-testid={`top-designer-${d.name}`}>
                  <td style={{ padding: "10px 12px" }}>{d.name}</td>
                  <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 600 }}>{d.audits}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <p style={{ color: "var(--v-text-2)", fontSize: 11, marginTop: 28, lineHeight: 1.6 }}>
        Page-view, traffic-source and device-breakdown analytics are tracked via Google Analytics 4.
        View those in your <a href="https://analytics.google.com" target="_blank" rel="noopener noreferrer" style={{ color: "var(--v-accent)" }}>GA4 console</a>.
        Analytics cookies only load once a visitor accepts the consent banner.
      </p>
    </div>
  );
}

function Kpi({ label, value, sub, highlight, testid }) {
  return (
    <div
      className="v-surface"
      data-testid={testid}
      style={{
        padding: "18px 20px",
        borderColor: highlight ? "var(--v-accent)" : "var(--v-border)",
        borderLeft: highlight ? "3px solid var(--v-accent)" : "1px solid var(--v-border)",
      }}
    >
      <div className="overline" style={{ color: "var(--v-text-2)" }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 300, letterSpacing: "-.02em", marginTop: 8, color: "var(--v-text)" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, marginTop: 6, color: "var(--v-text-2)" }}>{sub}</div>}
    </div>
  );
}

function EmptyHint({ children, style }) {
  return <div className="mono" style={{ color: "var(--v-text-2)", fontSize: 12, ...style }}>{children}</div>;
}
